import React from "react";
import { Check, Copy, Edit3, Folder as FolderIcon, Pin, Share2, Tags, Trash2, X } from "lucide-react";
import type { Folder as FolderType } from "../../api/notes";
import ModalShell from "./VaultModalShell";

export type VaultBulkActionSheet = "move" | "tag" | "share" | null;

type RailProps = {
    accentStyle: React.CSSProperties;
    selectedNotesCount: number;
    canEditSingle?: boolean;
    onEdit?: () => void;
    onMove: () => void;
    onCopy: () => void;
    onPin: () => void;
    onTag: () => void;
    onShare: () => void;
    onRecycle: () => void;
    onClear: () => void;
};

export function VaultBulkSelectionRail({
    accentStyle,
    selectedNotesCount,
    canEditSingle,
    onEdit,
    onMove,
    onCopy,
    onPin,
    onTag,
    onShare,
    onRecycle,
    onClear,
}: RailProps) {
    return (
        <div className="pointer-events-none fixed inset-x-3 bottom-24 z-40 md:bottom-6">
            <div
                className="pointer-events-auto mx-auto flex w-fit max-w-[calc(100vw-1.5rem)] items-center gap-1.5 overflow-x-auto rounded-2xl border bg-neutral-950/94 px-2 py-2 shadow-[0_0_44px_color-mix(in_srgb,var(--selection-accent)_22%,transparent)] backdrop-blur [border-color:color-mix(in_srgb,var(--selection-accent)_38%,#1f2937)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                style={accentStyle}
            >
                {canEditSingle && onEdit && (
                    <button type="button" onClick={onEdit} className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-cyan-100 transition hover:bg-cyan-950/25" title="Edit selected" aria-label="Edit selected">
                        <Edit3 size={15} />
                    </button>
                )}
                <button type="button" onClick={onMove} className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-[var(--selection-accent)] transition hover:bg-white/5" title="Move selected" aria-label="Move selected">
                    <FolderIcon size={15} />
                </button>
                <button type="button" onClick={onCopy} className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-neutral-200 transition hover:bg-neutral-900" title="Duplicate selected" aria-label="Duplicate selected">
                    <Copy size={15} />
                </button>
                <button type="button" onClick={onPin} className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-amber-100 transition hover:bg-amber-950/20" title="Pin selected" aria-label="Pin selected">
                    <Pin size={15} />
                </button>
                <button type="button" onClick={onTag} disabled={selectedNotesCount === 0} className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-[var(--selection-accent)] transition hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-35" title={selectedNotesCount === 0 ? "Select notes to tag" : "Tag selected notes"} aria-label="Tag selected notes">
                    <Tags size={15} />
                </button>
                <button type="button" onClick={onShare} disabled={selectedNotesCount === 0} className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-[var(--selection-accent)] transition hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-35" title={selectedNotesCount === 0 ? "Select notes to share" : "Share selected notes"} aria-label="Share selected notes">
                    <Share2 size={15} />
                </button>
                <button type="button" onClick={onRecycle} className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-rose-300 transition hover:bg-rose-950/25" title="Move selected to Recycle Bin" aria-label="Move selected to Recycle Bin">
                    <Trash2 size={15} />
                </button>
                <button type="button" onClick={onClear} className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-neutral-500 transition hover:bg-neutral-900 hover:text-white" title="Clear selection" aria-label="Clear selection">
                    <X size={16} />
                </button>
            </div>
        </div>
    );
}

type SheetsProps = {
    sheet: VaultBulkActionSheet;
    accent: string;
    accentStyle: React.CSSProperties;
    folders: FolderType[];
    selectedItemCount: number;
    selectedNotesCount: number;
    bulkFolder: string;
    bulkTags: string;
    bulkShareUsername: string;
    onClose: () => void;
    onSetBulkFolder: (folderId: string) => void;
    onSetBulkTags: (tags: string) => void;
    onSetBulkShareUsername: (username: string) => void;
    onMove: () => void;
    onTag: () => void;
    onShare: () => void;
};

export function VaultBulkActionSheets({
    sheet,
    accent,
    accentStyle,
    folders,
    selectedItemCount,
    selectedNotesCount,
    bulkFolder,
    bulkTags,
    bulkShareUsername,
    onClose,
    onSetBulkFolder,
    onSetBulkTags,
    onSetBulkShareUsername,
    onMove,
    onTag,
    onShare,
}: SheetsProps) {
    if (sheet === "move") {
        return (
            <ModalShell onClose={onClose} title="Move selected">
                <div className="space-y-4 p-5 sm:p-6" style={accentStyle}>
                    <div className="rounded-2xl border bg-black/25 p-4" style={{ borderColor: `${accent}55`, boxShadow: `0 0 28px ${accent}18` }}>
                        <p className="text-sm font-black text-white">{selectedItemCount} selected</p>
                        <p className="mt-1 text-xs font-semibold text-neutral-500">Choose where these items should live.</p>
                    </div>
                    <div className="grid max-h-72 gap-2 overflow-y-auto pr-1">
                        <button type="button" onClick={() => onSetBulkFolder("")} className={`flex items-center justify-between rounded-xl border px-4 py-3 text-left text-sm font-bold transition ${bulkFolder === "" ? "bg-black/55 text-white" : "border-neutral-800 bg-black/35 text-neutral-300 hover:border-neutral-600"}`} style={bulkFolder === "" ? { borderColor: `${accent}bb`, boxShadow: `0 0 18px ${accent}16` } : undefined}>
                            Unfiled
                            {bulkFolder === "" && <Check size={15} />}
                        </button>
                        {folders.map((folder) => (
                            <button type="button" key={folder.id} onClick={() => onSetBulkFolder(folder.id)} className={`flex items-center justify-between rounded-xl border px-4 py-3 text-left text-sm font-bold transition ${bulkFolder === folder.id ? "bg-black/55 text-white" : "border-neutral-800 bg-black/35 text-neutral-300 hover:border-neutral-600"}`} style={bulkFolder === folder.id ? { borderColor: `${accent}bb`, boxShadow: `0 0 18px ${accent}16` } : undefined}>
                                <span className="inline-flex min-w-0 items-center gap-3">
                                    <FolderIcon size={16} style={{ color: folder.color || "#22d3ee" }} />
                                    <span className="truncate">{folder.name}</span>
                                </span>
                                {bulkFolder === folder.id && <Check size={15} />}
                            </button>
                        ))}
                    </div>
                    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                        <button type="button" onClick={onClose} className="min-h-11 rounded-lg border border-neutral-800 px-4 text-sm font-bold text-neutral-300 hover:bg-neutral-900">Cancel</button>
                        <button type="button" onClick={onMove} className="min-h-11 rounded-lg px-4 text-sm font-black text-white" style={{ backgroundColor: accent, boxShadow: `0 0 22px ${accent}30` }}>Move</button>
                    </div>
                </div>
            </ModalShell>
        );
    }

    if (sheet === "tag") {
        return (
            <ModalShell onClose={onClose} title="Tag selected">
                <form className="space-y-4 p-5 sm:p-6" style={accentStyle} onSubmit={(event) => { event.preventDefault(); onTag(); }}>
                    <p className="text-sm text-neutral-500">Add tags to {selectedNotesCount} selected note{selectedNotesCount === 1 ? "" : "s"}.</p>
                    <input value={bulkTags} onChange={(event) => onSetBulkTags(event.target.value)} placeholder="launch, ideas, spaces" className="min-h-12 w-full rounded-xl border bg-black px-4 text-sm font-bold text-neutral-100 outline-none placeholder:text-neutral-600" style={{ borderColor: `${accent}66`, boxShadow: `0 0 20px ${accent}12` }} autoFocus />
                    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                        <button type="button" onClick={onClose} className="min-h-11 rounded-lg border border-neutral-800 px-4 text-sm font-bold text-neutral-300 hover:bg-neutral-900">Cancel</button>
                        <button type="submit" className="min-h-11 rounded-lg px-4 text-sm font-black text-white" style={{ backgroundColor: accent, boxShadow: `0 0 22px ${accent}30` }}>Add tags</button>
                    </div>
                </form>
            </ModalShell>
        );
    }

    if (sheet === "share") {
        return (
            <ModalShell onClose={onClose} title="Share selected">
                <form className="space-y-4 p-5 sm:p-6" style={accentStyle} onSubmit={(event) => { event.preventDefault(); onShare(); }}>
                    <p className="text-sm text-neutral-500">Grant view access to {selectedNotesCount} selected note{selectedNotesCount === 1 ? "" : "s"}.</p>
                    <input value={bulkShareUsername} onChange={(event) => onSetBulkShareUsername(event.target.value)} placeholder="@username" className="min-h-12 w-full rounded-xl border bg-black px-4 text-sm font-bold text-neutral-100 outline-none placeholder:text-neutral-600" style={{ borderColor: `${accent}66`, boxShadow: `0 0 20px ${accent}12` }} autoFocus />
                    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                        <button type="button" onClick={onClose} className="min-h-11 rounded-lg border border-neutral-800 px-4 text-sm font-bold text-neutral-300 hover:bg-neutral-900">Cancel</button>
                        <button type="submit" className="min-h-11 rounded-lg px-4 text-sm font-black text-white" style={{ backgroundColor: accent, boxShadow: `0 0 22px ${accent}30` }}>Share</button>
                    </div>
                </form>
            </ModalShell>
        );
    }

    return null;
}
